<template>
  <button
    type="button"
    class="control-chip"
    :class="{
      'control-chip--active': active,
      'control-chip--mid': mid,
      'control-chip--ghost': ghost,
      'control-chip--small': small,
    }"
    :style="rootStyle"
    @click="$emit('click', $event)">
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { sheetAccentStyle } from './sheetAccent';

const props = withDefaults(
  defineProps<{
    /** The chosen chip: accent fill, accent ink. */
    active?: boolean;
    /** The reached tint (`mid` of the sketch): a softer accent — the preset
     *  chips below the current level light up, the ones above stay idle. */
    mid?: boolean;
    /** The dimmed chip — an option that exists but is not offered. */
    ghost?: boolean;
    /** Denser chip for rows of many (fan speeds, sources). */
    small?: boolean;
    /** Overrides the sheet's accent for this chip only. */
    accent?: string;
  }>(),
  { active: false, mid: false, ghost: false, small: false },
);

defineEmits<{ click: [event: MouseEvent] }>();

const rootStyle = computed(() => sheetAccentStyle(props.accent));
</script>

<style scoped>
.control-chip {
  /* The sheet's accent, or the light accent when nothing has set one. */
  --chip-accent: var(--sheet-accent, var(--device-color-light));
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
  padding: calc(0.375rem * var(--ui-space-factor)) calc(0.9375rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-600));
  font: inherit;
  font-size: 0.7813rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.dark .control-chip {
  background: rgba(255, 255, 255, 0.03);
  color: rgb(var(--gray-300));
}

/* The dark base above is a (0,2,0) selector, so the state modifiers must carry
   the same weight here or their fill and ink lose the cascade (the border
   already passes: the dark base sets none). Declared before `:hover` so
   hovering still lifts the border, and in the same order as the light rules
   below — ghost, then active, then mid, so a chip carrying several modifiers
   resolves the same way in both themes (mid last: the reached tint is the
   calmer of the two). */
.dark .control-chip--ghost {
  /* The light ghost's dim recipe; without this variant the dark base wins and
     a ghost chip paints identical to an idle one (t_53eee7a5). */
  border-color: var(--glass-edge);
  color: rgb(var(--gray-400));
  background: transparent;
}

.dark .control-chip--active {
  background: color-mix(in srgb, var(--chip-accent) 16%, transparent);
  border-color: color-mix(in srgb, var(--chip-accent) 55%, transparent);
  color: var(--chip-accent);
}

.dark .control-chip--mid {
  background: color-mix(in srgb, var(--chip-accent) 24%, transparent);
  border-color: color-mix(in srgb, var(--chip-accent) 38%, transparent);
  color: color-mix(in srgb, var(--chip-accent) 85%, #fff);
}

.control-chip:hover {
  border-color: color-mix(in srgb, var(--chip-accent) 40%, var(--glass-edge));
}

.control-chip--small {
  padding: calc(0.25rem * var(--ui-space-factor)) calc(0.6875rem * var(--ui-space-factor));
  font-size: 0.7188rem;
}

.control-chip--ghost {
  border-color: var(--glass-edge);
  color: rgb(var(--gray-400));
  background: transparent;
}

.control-chip--active {
  background: color-mix(in srgb, var(--chip-accent) 16%, transparent);
  border-color: color-mix(in srgb, var(--chip-accent) 55%, transparent);
  color: var(--chip-accent);
}

/* Declared after `--active` on purpose: a chip can be both (an active preset
   inside the reached range) and the reached tint is the calmer of the two. */
.control-chip--mid {
  background: color-mix(in srgb, var(--chip-accent) 24%, transparent);
  border-color: color-mix(in srgb, var(--chip-accent) 38%, transparent);
  color: color-mix(in srgb, var(--chip-accent) 85%, #fff);
}

.control-chip:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--chip-accent) 60%, transparent);
  outline-offset: 2px;
}
</style>
